import { FC, PropsWithChildren, StrictMode, createContext, useContext, useEffect, useRef, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { getAuthenticatedUserInfo, logoutEndpoint, refreshToken, requestToken, setRefreshCookie } from 'services/auth';
import { isTokenExpired } from 'services/jwt';
import { Role } from 'types/preferences';
import { AuthResponse } from 'types/response';
import { UserProfile } from 'types/user-profile';

type AuthContextType = {
  userProfile: UserProfile;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile>>;
  userLoggedIn: () => boolean;
  login: (code: string | null) => Promise<boolean>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const [userProfile, setUserProfile] = useState<UserProfile>({
    username: '',
    email: '',
    role: Role.NONE,
    active: true,
    customers: [],
    truckingCompanies: [],
    locations: [],
    defaultLocation: undefined,
    columnPreferences: [],
    savedFiltersJson: undefined
  });

  const login: AuthContextType['login'] = async code => {
    if (!code) return false;

    const result: AuthResponse['AuthenticationResult'] | string = await requestToken(code);
    if (typeof result === 'string') return false;

    let authenticatedUserInfo: AuthResponse['userProfile'];
    try {
      authenticatedUserInfo = await getAuthenticatedUserInfo(result);
    } catch {
      return false;
    }

    if (!authenticatedUserInfo.active) return false;

    setRefreshCookie(result.refreshToken);
    localStorage.setItem('token', result.accessToken);
    localStorage.setItem('username', authenticatedUserInfo.username);
    localStorage.setItem('email', authenticatedUserInfo.email);

    const hasRole = Object.values(Role).find((role: string) => role === authenticatedUserInfo.role.toUpperCase());

    setUserProfile({
      username: authenticatedUserInfo.username,
      email: authenticatedUserInfo.email,
      role: hasRole ?? Role.NONE,
      active: true,
      customers: authenticatedUserInfo.customers,
      truckingCompanies: authenticatedUserInfo.truckingCompanies,
      locations: authenticatedUserInfo.locations,
      defaultLocation: authenticatedUserInfo.defaultLocation,
      columnPreferences: authenticatedUserInfo.columnPreferences,
      savedFiltersJson: (authenticatedUserInfo as any).savedFiltersJson
    });
    return true;
  };

  const logout = async (): Promise<void> => {
    localStorage.clear();
    sessionStorage.clear();
    await logoutEndpoint();
  };

  const userLoggedIn = (): boolean => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    return !isTokenExpired(token);
  };

  return (
    <AuthContext.Provider value={{ userProfile, setUserProfile, userLoggedIn, login, logout }}>
      <Outlet />
      {children}
    </AuthContext.Provider>
  );
};

export const RequireAuth = () => {
  const { userLoggedIn, logout } = useAuth();
  const refreshInFlight = useRef(false);
  const [refreshAttempted, setRefreshAttempted] = useState(false);

  useEffect(() => {
    if (userLoggedIn() || !localStorage.getItem('token') || refreshInFlight.current || refreshAttempted) return;

    let cancelled = false;
    refreshInFlight.current = true;

    refreshToken().then(token => {
      refreshInFlight.current = false;
      if (cancelled) return;
      if (!token || isTokenExpired(token)) void logout();
      setRefreshAttempted(true);
    });

    return () => {
      cancelled = true;
    };
  }, [logout, refreshAttempted, userLoggedIn]);

  if (userLoggedIn()) {
    return (
      <StrictMode>
        <Outlet />
      </StrictMode>
    ); /* moved strict mode here to avoid duplicate auth attempts in development */
  }

  if (localStorage.getItem('token') && !refreshAttempted) {
    return null;
  }

  if (refreshAttempted) {
    return (
      <Navigate
        to="/unauthorized"
        state={{ message: new Error('Your session has expired. Please sign in again.') }}
        replace
      />
    );
  }

  return <Navigate to={{ pathname: '/unauthorized' }} replace />;
};
